import { useParams, useSearchParams } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import useReadState from '@hooks/useReadState'
import exagoReportService, { exagoRefusal } from '@api/v1/exagoReportService'
import reportJobService from '@api/v1/reportJobService'
import SpecializedReportShell from '@components/reports/SpecializedReportShell'
import ExagoAgedArReport from './ExagoAgedArReport'
import ExagoGenericReport, { ExagoRefusalPanel } from './ExagoGenericReport'
import { ADVANCED_REPORT_NAME } from './exagoPromptFields'
import { savedExagoKind } from './exagoAgedArJob'

/**
 * Spec 209 §10: `/reports/exago/:exagoReportId` decides which page reads the report.
 *
 *   no `?jobId=`  `/info` decides: `reviewed` → spec 190's page, untouched; otherwise the
 *                 generic page, handed the description.
 *   `?jobId=`     the SAVED RESULT decides, by its own `kind`: 'generic' → the generic page
 *                 in reopen mode, handed the body; anything else → spec 190's page, which
 *                 reads it as it always has. A saved result that cannot be read decides
 *                 nothing: `/info` decides, as above, and the page it picks says why.
 *
 * It never waits for the Advanced Reports list. Neither read draws anything: the body read
 * here only picks the page, and each page's reopen still passes spec 168's guard
 * (ReopenTargetContext) before a row commits. While a read is out: the shell, titled
 * "Advanced Report", with a status line.
 */

const UNKNOWN_FAILURE = 'Report Center couldn’t open this Advanced Report.'

/*
 * A worker without spec 209 has no `/info` route: Hono answers a bare 404 with no `code`.
 * That is not a refusal of THIS report, so the open is exactly what it was before spec 209
 * — spec 190's page, which runs only the reviewed adapter and refuses any other id itself.
 * A coded 404 (EXAGO_REPORT_NOT_AVAILABLE) is cb-api's answer and is shown as such.
 */
const isPreInfoWorker = (err) => {
  const res = err?.response
  return res?.status === 404 && !(res.data && typeof res.data === 'object' && typeof res.data.code === 'string')
}
const OFFLINE = 'Can’t reach the server, so this report hasn’t opened. Check your connection and try again.'

/*
 * A 422 EXAGO_PROMPT_UNSTATED sentence quotes the firm's own prompt label ("This report asks
 * for “<label>”…"), so its band carries PENDO_IGNORE, as the same refusal inside the generic
 * page does (spec 209 §3). The heading here is the generic word, so `privateName` cannot.
 */
const RouteShell = ({ status, error, refusal, onRetry }) => (
  <SpecializedReportShell
    name={ADVANCED_REPORT_NAME}
    running={false}
    error={error}
    ready={false}
    privateError={refusal?.code === 'EXAGO_PROMPT_UNSTATED'}
  >
    {status && (
      <p role="status" className="rounded-rc-card border border-rc-line bg-white p-6 text-sm text-rc-muted" data-testid="exago-route-status">
        {status}
      </p>
    )}
    {error && <ExagoRefusalPanel refusal={refusal} onRetry={onRetry} />}
  </SpecializedReportShell>
)

/**
 * No `?jobId=`: the report's own description picks the page.
 *
 * Also the fall-through for a `?jobId=` whose saved result could NOT be read (`jobId` set):
 * the page `/info` picks reads the run itself and says why it can't open it INSIDE the
 * page, with the filter bar and Run beside the sentence — never a dead end whose only
 * button re-reads a refusal (410 gone, 403 not yours) that cannot change.
 *   reviewed → spec 190's page, which reads `?jobId=` itself exactly as before spec 209
 *              (savedRunErrorSentence, else cb-api's own `response.data.error`). So does a
 *              worker without `/info` (the bare 404 above).
 *   generic  → the generic page in reopen mode WITHOUT a body: its reopen reads the run,
 *              and its error band says savedRunErrorSentence, else cb-api's own
 *              `response.data.error`, else the code's sentence (exagoRefusal) — the page's
 *              reopenErrorMessage. A failed reopen shows no refusal panel and no "Try again";
 *              the filter bar's Run starts a fresh run and never re-reads the saved one.
 * A refused `/info` is said in the shell, as on a live open; its Try again asks `/info` again.
 */
const LiveOpen = ({ exagoReportId, jobId = null }) => {
  const query = useQuery({
    queryKey: ['exago-info', String(exagoReportId).toLowerCase()],
    queryFn: () => exagoReportService.info(exagoReportId),
    // A refusal is an answer (404 not shared, 422 a prompt Report Center cannot state):
    // asking again three times would only triple a ~30 s wait. "Try again" asks again.
    retry: false,
    staleTime: 60_000,
  })
  const state = useReadState('exago-info', query)
  if (state === 'failed' && isPreInfoWorker(query.error)) return <ExagoAgedArReport />
  if (state === 'failed') {
    const refusal = exagoRefusal(query.error, 'info')
    return <RouteShell error={refusal?.sentence || UNKNOWN_FAILURE} refusal={refusal} onRetry={() => query.refetch()} />
  }
  if (state === 'blocked') return <RouteShell error={OFFLINE} refusal={null} onRetry={() => query.refetch()} />
  if (state !== 'ready') return <RouteShell status="Opening the report from Advanced Reports…" />
  if (query.data.reviewed) return <ExagoAgedArReport />
  return <ExagoGenericReport exagoReportId={exagoReportId} info={query.data} jobId={jobId} />
}

/**
 * `?jobId=`: the saved result's own kind picks the page — when it can be READ. A read that
 * fails (410 gone, 403 EXAGO_OWNER_ONLY, 404, 409, 503) picks nothing: it falls through to
 * LiveOpen's choice, carrying the jobId, so the chosen page says why inside itself. Before
 * spec 209 the route element was spec 190's page, which did exactly that.
 */
const SavedOpen = ({ exagoReportId, jobId }) => {
  const query = useQuery({
    queryKey: ['exago-saved-run', jobId],
    queryFn: () => reportJobService.result(jobId),
    retry: false,
    // One read per open; a later open of the same run reads again (gcTime 0).
    staleTime: Infinity,
    gcTime: 0,
  })
  const state = useReadState('exago-saved-run', query)
  if (state === 'failed') return <LiveOpen exagoReportId={exagoReportId} jobId={jobId} />
  // Offline: nothing was asked, so asking again is the honest way on.
  if (state === 'blocked') return <RouteShell error={OFFLINE} refusal={null} onRetry={() => query.refetch()} />
  if (state !== 'ready') return <RouteShell status="Opening the saved result…" />
  if (savedExagoKind(query.data) === 'generic') {
    return <ExagoGenericReport exagoReportId={exagoReportId} jobId={jobId} savedBody={query.data} />
  }
  return <ExagoAgedArReport />
}

const ExagoReportRoute = () => {
  const { exagoReportId } = useParams()
  const [searchParams] = useSearchParams()
  const jobId = searchParams.get('jobId') || null
  return jobId
    ? <SavedOpen key={`${exagoReportId}:${jobId}`} exagoReportId={exagoReportId} jobId={jobId} />
    : <LiveOpen key={exagoReportId} exagoReportId={exagoReportId} />
}

export default ExagoReportRoute
